import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostListener,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { AppTextService } from '@fi-shared/app-text';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { FiChatSourceMessageType } from '@fi-shared/fi-chat/fi-chat-source-message-type';
import { ITextEditorLinkAttachment } from '@fi-shared/fi-text-editor/fi-text-editor';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiImageSize } from '@fi-shared/types';
import { isDefined } from '@fi-shared/utils';
import { IChatConversationMessage } from '@fi-shared/fi-chat/fi-chat';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiChatConversationHelperService } from '../../fi-chat-conversation-helper.service';

@Component({
    selector: 'fi-context-message-preview',
    templateUrl: './context-message-preview.component.html',
    styleUrls: ['./context-message-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-context-message-preview' },
    standalone: false,
})
export class FiContextMessagePreviewComponent {
    @Input({ required: true }) conversationMessage: IChatConversationMessage;
    @Input({ required: true }) sourceMessage: IChatConversationMessage;
    @Input() isOutgoingMessage: boolean;

    @Output() scrollToMessage = new EventEmitter<IChatConversationMessage>();
    @Output() cancelEditing = new EventEmitter<void>();
    protected readonly FiImageSize = FiImageSize;

    constructor(
        private appTextService: AppTextService,
        private fiUserDataService: FiUserDataService,
        private fiChatHelperService: FiChatHelperService,
        private fiChatConversationHelperService: FiChatConversationHelperService,
    ) {}

    private get contextMessage(): IChatConversationMessage {
        return this.sourceMessage || this.conversationMessage;
    }

    @HostListener('click')
    protected onClick(): void {
        this.scrollToMessage.emit(this.contextMessage);
    }

    protected isAttachmentsListShown(): boolean {
        return this.fiChatHelperService.hasAttachments(this.contextMessage);
    }

    protected getEditorLabel(): string {
        if (!isDefined(this.sourceMessage)) {
            return this.appTextService.get('common.edit_message');
        }

        if (this.conversationMessage.SourceMessageType === FiChatSourceMessageType.Reply)
            return this.isOutgoingMessage
                ? this.appTextService.get('common.reply_to_yourself')
                : this.appTextService.get('common.reply_to_user', {
                      '{0}': this.conversationMessage.UserProfile.FullName,
                  });

        if (this.conversationMessage.SourceMessageType === FiChatSourceMessageType.Forwarded)
            return this.sourceMessage.UserProfile.Id === this.fiUserDataService.getUserId()
                ? this.appTextService.get('chat.forward_message_from_yourself')
                : this.appTextService.get('chat.forward_from_user', {
                      '{0}': this.sourceMessage.UserProfile.FullName,
                  });
    }

    protected getMessageContent(): string | SafeHtml {
        const messageContent: string =
            isDefined(this.sourceMessage) && this.isSourceMessageContext()
                ? this.sourceMessage.Content
                : this.conversationMessage.Content;

        if (this.isOnlyAttachmentsMessage()) {
            const attachmentsCount = this.fiChatConversationHelperService.getAttachmentsCount(this.contextMessage);
            return this.fiChatConversationHelperService.getAttachmentsCountLabel(attachmentsCount);
        }

        return this.fiChatHelperService.getMessageContent(messageContent);
    }

    protected isOnlyAttachmentsMessage(): boolean {
        return this.fiChatConversationHelperService.isOnlyAttachmentsMessage(this.contextMessage);
    }

    protected onCancelEditing(): void {
        this.cancelEditing.emit();
    }

    protected getEditorAttachment(): IMediaBankFileReference {
        return this.contextMessage.MediaBankFileReferences[0];
    }

    protected getEditorLinkAttachment(): ITextEditorLinkAttachment {
        return !!this.contextMessage.MediaBankFileReferences?.length ? undefined : this.contextMessage.Links[0];
    }

    protected getMediaBankCount(): number {
        return this.contextMessage.MediaBankFileReferences?.length || 0;
    }

    private isSourceMessageContext(): boolean {
        return (
            this.conversationMessage.SourceMessageType === FiChatSourceMessageType.Reply ||
            this.conversationMessage.SourceMessageType === FiChatSourceMessageType.Forwarded
        );
    }
}
